/**
 * features/finance/UnidentifiedPayments.tsx
 * ------------------------------------------------------------------
 * Suspense account for bank credits that arrive WITHOUT a usable
 * reference — money received but not yet attributable to a student.
 *
 *   • Capture a credit (date, amount, method, bank ref, narration).
 *   • Each open row is inline-editable (commit on blur) while you chase
 *     the real payer.
 *   • When the destination is known, "Settle" opens a modal to pick the
 *     student's invoice: a normal Payment is captured against it (invoice
 *     balance updates, receipt minted) and this record is flagged settled,
 *     linked to that payment. Every settle is audited (finance log +
 *     Activity Log). Nothing here touches an invoice until settled.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { HelpCircle, Search, X, Plus, Trash2, CheckCircle2, Landmark } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { FormModal, modalPrimaryBtn, modalGhostBtn, NumberField } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { termLabel } from '@/lib/school.config';
import {
  applyPayment, formatReceiptNo, fmtPula, fmtBalance, round2, PAYMENT_METHOD_LABELS,
  type Invoice, type PaymentMethod, type UnidentifiedPayment,
} from '@/lib/financeCore';

const METHODS: PaymentMethod[] = ['eft', 'card', 'cash', 'mobile', 'other'];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function UnidentifiedPayments() {
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email, name: userData?.displayName };

  // Capture form
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>('eft');
  const [bankRef, setBankRef] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  const [showSettled, setShowSettled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  // Settle modal
  const [target, setTarget] = useState<UnidentifiedPayment | null>(null);
  const [invQuery, setInvQuery] = useState('');
  const [pickInvoiceId, setPickInvoiceId] = useState('');
  const [settleNote, setSettleNote] = useState('');

  const rows = data.unidentifiedPayments;
  const open = useMemo(() => rows.filter((r) => r.status === 'open').sort((a, b) => (b.receivedAt ?? '').localeCompare(a.receivedAt ?? '')), [rows]);
  const settled = useMemo(() => rows.filter((r) => r.status === 'settled').sort((a, b) => (b.settledAt ?? '').localeCompare(a.settledAt ?? '')), [rows]);
  const suspenseTotal = useMemo(() => round2(open.reduce((a, r) => a + (r.amount || 0), 0)), [open]);

  const toast = (status: 'saving' | 'success' | 'error', message: string) => setSc({ show: true, status, message });

  const addRow = async () => {
    if (amount <= 0 || busy) return;
    setBusy(true); toast('saving', 'Adding credit…');
    try {
      await finance.unidentifiedPayments.add({
        amount: round2(amount), method, receivedAt: date || todayISO(),
        bankRef: bankRef.trim() || undefined, description: description.trim() || undefined,
        status: 'open', createdBy: actor.id, createdByName: actor.name ?? undefined,
      });
      await recordFinanceAudit({ action: 'unidentified.capture', actor, before: undefined, after: { amount: round2(amount), method, bankRef: bankRef.trim() || undefined, receivedAt: date || todayISO() } });
      setAmount(0); setBankRef(''); setDescription(''); setDate(todayISO()); setAdding(false);
      toast('success', `${fmtPula(amount)} added to suspense.`);
    } catch (e) { console.error(e); toast('error', 'Could not add the credit — please retry.'); }
    finally { setBusy(false); }
  };

  const patchRow = async (id: string, patch: Record<string, unknown>) => {
    try { await finance.unidentifiedPayments.update(id, patch); }
    catch (e) { console.error(e); toast('error', 'Could not save the edit — please retry.'); }
  };

  const deleteRow = async (r: UnidentifiedPayment) => {
    if (!window.confirm(`Delete this unidentified credit of ${fmtPula(r.amount)}? This does not affect any invoice.`)) return;
    setBusy(true); toast('saving', 'Deleting…');
    try {
      await finance.unidentifiedPayments.delete(r.id);
      await recordFinanceAudit({ action: 'unidentified.delete', actor, subjectId: r.id, before: { amount: r.amount, method: r.method, bankRef: r.bankRef, receivedAt: r.receivedAt } });
      toast('success', 'Credit removed.');
    } catch (e) { console.error(e); toast('error', 'Could not delete — please retry.'); }
    finally { setBusy(false); }
  };

  // Settle -------------------------------------------------------------
  const invById = useMemo(() => { const m: Record<string, Invoice> = {}; data.invoices.forEach((i) => { m[i.id] = i; }); return m; }, [data.invoices]);
  const invoiceMatches = useMemo(() => {
    const q = invQuery.trim().toLowerCase();
    if (!q) return [];
    return data.invoices
      .filter((i) => i.status !== 'void')
      .filter((i) => (i.studentName ?? '').toLowerCase().includes(q) || i.invoiceNo.toLowerCase().includes(q))
      .sort((a, b) => Number(b.balance > 0) - Number(a.balance > 0) || (a.studentName ?? '').localeCompare(b.studentName ?? ''))
      .slice(0, 30);
  }, [data.invoices, invQuery]);
  const pickedInv = pickInvoiceId ? invById[pickInvoiceId] : undefined;

  const openSettle = (r: UnidentifiedPayment) => { setTarget(r); setInvQuery(''); setPickInvoiceId(''); setSettleNote(''); };
  const closeSettle = () => { if (!busy) { setTarget(null); setInvQuery(''); setPickInvoiceId(''); setSettleNote(''); } };

  const settle = async () => {
    if (!target || !pickedInv || busy) return;
    setBusy(true); toast('saving', 'Settling payment…');
    try {
      const u = target; const inv = pickedInv;
      const now = new Date().toISOString();
      const seq = data.payments.length + 1;
      const receiptNo = formatReceiptNo(inv.year, seq);
      const upd = applyPayment(inv, u.amount);
      const payId = await finance.payments.add({
        invoiceId: inv.id, studentId: inv.studentId, studentName: inv.studentName, familyId: inv.familyId,
        amount: round2(u.amount), method: u.method, reference: u.bankRef || undefined, receiptNo,
        receivedAt: u.receivedAt || now, recordedBy: actor.id, recordedByName: actor.name ?? undefined,
        note: `Allocated from unidentified bank credit${settleNote.trim() ? ` — ${settleNote.trim()}` : ''}`,
      });
      await finance.invoices.update(inv.id, upd);
      await finance.unidentifiedPayments.update(u.id, {
        status: 'settled', settledInvoiceId: inv.id, settledInvoiceNo: inv.invoiceNo,
        settledStudentName: inv.studentName ?? undefined, settledPaymentId: payId,
        settledAt: now, settledBy: actor.id, settledByName: actor.name ?? undefined,
      });
      await recordFinanceAudit({
        action: 'unidentified.settle', actor, subjectId: inv.id,
        before: { unidentifiedId: u.id, amount: u.amount, bankRef: u.bankRef, receivedAt: u.receivedAt },
        after: { invoiceId: inv.id, invoiceNo: inv.invoiceNo, studentName: inv.studentName, paymentId: payId, receiptNo, newBalance: upd.balance },
      });
      toast('success', `Allocated ${fmtPula(u.amount)} to ${inv.studentName ?? 'student'} — receipt ${receiptNo}, new balance ${fmtBalance(upd.balance)}.`);
      closeSettle();
    } catch (e) { console.error(e); toast('error', 'Could not settle the payment — please retry.'); }
    finally { setBusy(false); }
  };

  const projected = target && pickedInv ? applyPayment(pickedInv, target.amount) : null;

  if (data.loading) {
    return <SoftCard hover={false}><p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">Loading…</p></SoftCard>;
  }

  return (
    <div className="space-y-6">
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="amber" value={fmtPula(suspenseTotal)} label="Held in suspense" icon={Landmark} />
        <StatChip tone="slate" value={String(open.length)} label="Open credits" icon={HelpCircle} />
        <StatChip tone="emerald" value={String(settled.length)} label="Settled" icon={CheckCircle2} />
      </div>

      {/* Capture */}
      <SoftCard hover={false}>
        {adding ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Date received</span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={glassFieldClass('py-2')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Amount</span>
                <NumberField value={amount} onValue={setAmount} className={glassFieldClass('py-2')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Method</span>
                <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} className={glassFieldClass('py-2')}>
                  {METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Bank reference</span>
                <input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="As on the statement" className={glassFieldClass('py-2')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Narration / payer</span>
                <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Free text" className={glassFieldClass('py-2')} />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button className={modalGhostBtn} onClick={() => setAdding(false)} disabled={busy}>Cancel</button>
              <button className={modalPrimaryBtn} onClick={addRow} disabled={busy || amount <= 0}>{busy ? 'Adding…' : 'Add credit'}</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Bank credits with no usable reference sit here until you find the payer, then <span className="font-medium">Settle</span> allocates them to the right invoice.
            </div>
            <button className={modalPrimaryBtn} onClick={() => setAdding(true)}><Plus size={15} /> Add credit</button>
          </div>
        )}
      </SoftCard>

      {/* Open credits — editable rows */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <HelpCircle size={15} className="text-amber-500" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Open credits — {open.length}</h3>
        </div>
        <div className="p-4 overflow-x-auto">
          {open.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Nothing in suspense. Add a credit above when the bank shows a payment you can't place.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 px-3 font-medium text-right">Amount</th>
                  <th className="py-2 px-3 font-medium">Method</th>
                  <th className="py-2 px-3 font-medium">Bank reference</th>
                  <th className="py-2 px-3 font-medium">Narration / payer</th>
                  <th className="py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {open.map((r) => (
                  <tr key={r.id} className="align-middle">
                    <td className="py-1.5 pr-3">
                      <input type="date" defaultValue={(r.receivedAt ?? '').slice(0, 10)} onBlur={(e) => { const v = e.target.value; if (v && v !== (r.receivedAt ?? '').slice(0, 10)) patchRow(r.id, { receivedAt: v }); }} className={glassFieldClass('py-1 text-xs w-36')} />
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      <input inputMode="decimal" defaultValue={String(r.amount ?? 0)} onBlur={(e) => { const n = round2(Number(e.target.value) || 0); if (n > 0 && n !== round2(r.amount)) patchRow(r.id, { amount: n }); }} className={glassFieldClass('py-1 text-xs w-28 text-right')} />
                    </td>
                    <td className="py-1.5 px-3">
                      <select value={r.method} onChange={(e) => patchRow(r.id, { method: e.target.value })} className={glassFieldClass('py-1 text-xs')}>
                        {METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 px-3">
                      <input defaultValue={r.bankRef ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.bankRef ?? '')) patchRow(r.id, { bankRef: v || undefined }); }} className={glassFieldClass('py-1 text-xs w-40')} />
                    </td>
                    <td className="py-1.5 px-3">
                      <input defaultValue={r.description ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.description ?? '')) patchRow(r.id, { description: v || undefined }); }} className={glassFieldClass('py-1 text-xs w-48')} />
                    </td>
                    <td className="py-1.5 pl-3 text-right whitespace-nowrap">
                      <button onClick={() => openSettle(r)} className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#0d9488)] hover:underline mr-3">
                        <CheckCircle2 size={13} /> Settle
                      </button>
                      <button onClick={() => deleteRow(r)} className="inline-flex items-center text-xs text-gray-400 hover:text-rose-600" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </SoftCard>

      {/* Settled history */}
      {settled.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <button onClick={() => setShowSettled((v) => !v)} className="w-full px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 text-left">
            <CheckCircle2 size={15} className="text-emerald-500" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex-1">Settled — {settled.length}</h3>
            <span className="text-xs text-gray-400">{showSettled ? 'Hide' : 'Show'}</span>
          </button>
          {showSettled && (
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-3 font-medium">Received</th>
                    <th className="py-2 px-3 font-medium text-right">Amount</th>
                    <th className="py-2 px-3 font-medium">Allocated to</th>
                    <th className="py-2 px-3 font-medium">Invoice</th>
                    <th className="py-2 pl-3 font-medium">Settled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {settled.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{(r.receivedAt ?? '').slice(0, 10)}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtPula(r.amount)}</td>
                      <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{r.settledStudentName ?? '—'}</td>
                      <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{r.settledInvoiceNo ?? '—'}</td>
                      <td className="py-2 pl-3 text-gray-500 dark:text-gray-400">{(r.settledAt ?? '').slice(0, 10)}{r.settledByName ? ` · ${r.settledByName}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SoftCard>
      )}

      {/* Settle modal */}
      {target && (
        <FormModal
          icon={CheckCircle2}
          title="Settle unidentified payment"
          subtitle={`${fmtPula(target.amount)} · ${(target.receivedAt ?? '').slice(0, 10)}${target.bankRef ? ` · ${target.bankRef}` : ''}`}
          onClose={closeSettle}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button className={modalGhostBtn} onClick={closeSettle} disabled={busy}>Cancel</button>
              <button className={modalPrimaryBtn} onClick={settle} disabled={busy || !pickedInv}>
                {busy ? 'Settling…' : pickedInv ? `Allocate ${fmtPula(target.amount)}` : 'Pick an invoice'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {target.description && <p className="text-xs text-gray-500 dark:text-gray-400">Narration: <span className="text-gray-700 dark:text-gray-200">{target.description}</span></p>}

            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input value={invQuery} onChange={(e) => { setInvQuery(e.target.value); setPickInvoiceId(''); }} placeholder="Find the student's invoice — name or invoice #" className={glassFieldClass('py-2 pl-8 pr-8 text-sm')} autoFocus />
              {invQuery && <button onClick={() => { setInvQuery(''); setPickInvoiceId(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
            </div>

            {invQuery && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50">
                {invoiceMatches.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 p-3">No matching invoices.</p>
                ) : invoiceMatches.map((i) => {
                  const picked = i.id === pickInvoiceId;
                  return (
                    <button key={i.id} onClick={() => setPickInvoiceId(i.id)} className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 ${picked ? 'bg-[var(--accent-link,#0d9488)]/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800 dark:text-gray-100 truncate">{i.studentName ?? '—'}</div>
                        <div className="text-xs text-gray-400">{i.invoiceNo} · {termLabel(i.term)} {i.year}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-gray-400">Balance</div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{fmtBalance(i.balance)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {pickedInv && projected && (
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 p-3 text-sm">
                <div className="text-gray-700 dark:text-gray-200">
                  Allocating <span className="font-semibold">{fmtPula(target.amount)}</span> to <span className="font-medium">{pickedInv.studentName}</span> ({pickedInv.invoiceNo}).
                </div>
                <div className="text-gray-600 dark:text-gray-300 mt-1">
                  Balance {fmtBalance(pickedInv.balance)} → <span className="font-semibold">{fmtBalance(projected.balance)}</span>
                  {projected.balance < 0 && <span className="text-amber-600 dark:text-amber-300"> (credit of {fmtPula(-projected.balance)})</span>}
                </div>
              </div>
            )}

            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Note (optional)</span>
              <input value={settleNote} onChange={(e) => setSettleNote(e.target.value)} placeholder="e.g. Confirmed with parent by phone" className={glassFieldClass('py-2')} />
            </label>
          </div>
        </FormModal>
      )}
    </div>
  );
}
